import {
  type AgaveCostCode,
  type AgaveCostType,
  type AgaveGlCode,
  type AgaveProject,
  type AgaveSubJob,
  type AgaveVendor,
  type AgaveLocation,
  type AgaveDepartment,
  type AgaveSubaccount,
  type AgaveEmployeeEntity,
  type AgaveEquipment,
  type AgaveEmCostCode,
  type AgaveEmCostType,
  type FlexCode,
  type FlexCostCode,
  type FlexCostType,
  type FlexGlCode,
  type FlexVendor,
  type HighnoteTransactionEvent,
  HighnoteTransactionStatus,
  type ReceiptUpload,
  WorkflowStatus,
} from 'API';
import { useCanEditTransaction } from 'app/hooks/transactions/useCanEditTransaction';
import { useAuthorizationDate } from 'app/hooks/transactions/useAuthorizationDate';
import { formatDateInEastern, formatDateInEasternWithDateFns } from 'util/dateUtils';
import { useFuelCaptureState } from 'app/hooks/useFuelCapture';
import { NumericInput } from 'app/shared-components/forms';
import { Box, Button, FormControl, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { captureException } from '@sentry/react';
import { fileAccessService } from 'app/services/fileAccess.service';
import { useAccountType } from 'app/hooks/useAccountType';
import { useCodingDisplay } from 'app/hooks/useCodingDisplay';
import { useTransactionReceipts } from 'app/hooks/useTransactionReceipts';
import { useDeleteReceipt } from 'app/hooks/useDeleteReceipt';
import { useCodingSystemConfig } from 'app/hooks/transactions/useCodingSystemConfig';
import { useFlexStructuredMode } from 'app/hooks/useFlexStructuredMode';
import { MobileCodingFieldList } from 'app/pages/mobile/components/MobileCodingFieldList';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import MobileNoteInput from 'app/pages/mobile/components/mobile-option-menu/MobileNoteInput';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES, BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';
import { formatCurrency } from 'app/shared-components/util/Currency';
import { useAppSelector, useAppDispatch } from 'app/store';
import { clearAllFlexCodingSearches } from 'app/pages/store/flexCodingSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectAgaveSlice } from 'app/pages/store/agaveSlice';
import { agaveService } from 'app/services/agave.service';
import { useSnackbar } from 'notistack';
import { isReturned } from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { isTransactionFinalized } from 'app/services/agave/erpUtils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MobileCodingMenu from './MobileCodingMenu';
import { useSpectrumPostingVendorLock } from 'app/hooks/useSpectrumPostingVendorLock';
import { type CodingOptionKey } from 'app/pages/mobile/types/mobileCodingTypes';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import {
  CODING_COLUMNS_BY_TYPE,
  resolveDisplayCodingType,
  type CodingColumnId,
  type CodingType,
} from 'app/util/columnVisibility';
import {
  describeSubmitBlockers,
  getSubmitBlockers,
  isSubmitBlocked,
  submitBlockerItems,
  type SubmitBlockers,
} from 'util/submitRequirements';
import MobileReceiptPreview from './MobileReceiptPreview';
import { useTransactionForm, type TransactionFormValues } from './MobileTransactionForm';
import { type ErpPostingAccount } from 'app/services/erp-connection.service';
import TransactionField from './TransactionField';
import { sentryMask } from 'sentryReplayPrivacy';

type CodingOptionType =
  | FlexGlCode
  | FlexCode
  | FlexCostCode
  | FlexCostType
  | FlexVendor
  | AgaveGlCode
  | AgaveProject
  | AgaveCostCode
  | AgaveCostType
  | AgaveVendor
  | AgaveSubJob
  | AgaveLocation
  | AgaveDepartment
  | AgaveSubaccount
  | AgaveEmployeeEntity
  | AgaveEquipment
  | AgaveEmCostCode
  | AgaveEmCostType
  | ErpPostingAccount;

type Step = 'details_receipt' | 'coding' | 'fuel_capture' | 'review';

interface MobileEditTransactionSheetProps {
  open: boolean;
  onClose: () => void;
  transaction?: HighnoteTransactionEvent;
  // Fired after a save attempt so the parent list can reconcile with the server.
  // The mobile Actions queue is computed server-side (receipt presence is a live
  // DynamoDB check, not the OpenSearch index), so optimistic field patches alone
  // can't reliably decide whether a transaction still needs action — we re-query.
  onSaved?: () => void;
}

const MobileEditTransactionSheet = ({
  open,
  onClose,
  transaction,
  onSaved,
}: MobileEditTransactionSheetProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { deleteReceipt } = useDeleteReceipt();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { selectedPaidolId, selectedPaidol } = useAppSelector(selectUserCompanies);
  const { hasErpCodingUI, isConstructionType, isFlexCodingEnabled } = useAccountType();
  const { getDisplayName } = useCodingDisplay();
  const { isStructuredMode } = useFlexStructuredMode();
  const { agaveAccount } = useAppSelector(selectAgaveSlice);
  const { authorizationDate } = useAuthorizationDate(transaction);

  // Column visibility - single source of truth for which fields to show
  const { isVisible, getLabel } = useCodingSystemConfig();
  const [currentStep, setCurrentStep] = useState<Step>('details_receipt');
  const [optionMenuOpen, setOptionMenuOpen] = useState(false);
  const [currentField, setCurrentField] = useState<CodingOptionKey | null>(null);
  // Each loaded image keeps its receipt, so a tap on image N deletes that receipt even when
  // another receipt's URL failed to load and was left out of the preview.
  const [receiptImages, setReceiptImages] = useState<Array<{ receipt: ReceiptUpload; url: string }>>([]);
  const receiptUrls = useMemo(() => receiptImages.map((image) => image.url), [receiptImages]);
  const [note, setNote] = useState<string>(transaction?.note ?? '');
  const [isDeletingReceipt, setIsDeletingReceipt] = useState(false);
  // SEE-1091 — selected coding type for the equipment-gated type selector.
  // Null = nothing picked yet (renders only the selector + Notes, per Figma).
  const [selectedCodingType, setSelectedCodingType] = useState<CodingType | null>(() =>
    resolveDisplayCodingType(transaction)
  );

  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);

  // Fetch receipts using the hook (uses ReceiptUpload and TransactionToReceiptUpload)
  const { receipts, refetch: refetchReceipts } = useTransactionReceipts(transaction?.id, undefined);
  const { form, onSubmit, isSubmitting } = useTransactionForm({
    transaction,
    paidolId: selectedPaidolId,
    hasLinkedReceipts: receipts.length > 0,
    onClose,
  });
  // onSubmit closes the sheet before its promise resolves (so isSubmitting flips
  // off a frame before the sheet unmounts). Keep the overlay up across the whole
  // save+close so the review page never flashes back looking like a failure.
  const [isSaving, setIsSaving] = useState(false);

  const scopeId = `edit-transaction-${transaction?.id}`;
  const { setValue, control } = form as any; // TODO: Fix type casting

  // Watch coding fields for reactive updates when values are cleared or changed
  const watchedCodingValues = useWatch({
    control,
    name: [
      'glCode',
      'projectCode',
      'costCode',
      'costType',
      'subJob',
      'vendor',
      'location',
      'department',
      'subaccount',
      'employee',
      'billableItem',
      'equipment',
      'emCostCode',
      'emCostType',
    ],
  }) as [
    TransactionFormValues['glCode'],
    TransactionFormValues['projectCode'],
    TransactionFormValues['costCode'],
    TransactionFormValues['costType'],
    TransactionFormValues['subJob'],
    TransactionFormValues['vendor'],
    TransactionFormValues['location'],
    TransactionFormValues['department'],
    TransactionFormValues['subaccount'],
    TransactionFormValues['employee'],
    TransactionFormValues['billableItem'],
    TransactionFormValues['equipment'],
    TransactionFormValues['emCostCode'],
    TransactionFormValues['emCostType']
  ];

  const [
    watchedGlCode,
    watchedProjectCode,
    watchedCostCode,
    watchedCostType,
    watchedSubJob,
    watchedVendor,
    watchedLocation,
    watchedDepartment,
    watchedSubaccount,
    watchedEmployee,
    watchedBillableItem,
    watchedEquipment,
    watchedEmCostCode,
    watchedEmCostType,
  ] = watchedCodingValues;

  // The coding as entered, by column: what the steps list and what the submit rule reads.
  const watchedByColumn = useMemo(
    () => ({
      glCode: watchedGlCode,
      project: watchedProjectCode,
      subJob: watchedSubJob,
      costCode: watchedCostCode,
      costType: watchedCostType,
      vendor: watchedVendor,
      location: watchedLocation,
      department: watchedDepartment,
      subaccount: watchedSubaccount,
      employee: watchedEmployee,
      billableItem: watchedBillableItem,
      equipment: watchedEquipment,
      emCostCode: watchedEmCostCode,
      emCostType: watchedEmCostType,
    }),
    [
      watchedGlCode,
      watchedProjectCode,
      watchedSubJob,
      watchedCostCode,
      watchedCostType,
      watchedVendor,
      watchedLocation,
      watchedDepartment,
      watchedSubaccount,
      watchedEmployee,
      watchedBillableItem,
      watchedEquipment,
      watchedEmCostCode,
      watchedEmCostType,
    ]
  );
  const stagedReceiptFile = useWatch({ control, name: 'receiptFile' });

  // Determine which ERP system is active
  const useAgave = hasErpCodingUI && isConstructionType;
  const useFlex = isFlexCodingEnabled;

  // SEE-1091 — the type selector is flag-gated (matches the web transaction
  // sidesheet, which gates on the flag only). When the flag is off the selector
  // is hidden and the coding list renders flat as before. Equipment is a Vista
  // concept, but the flag is the control — admins only enable it for Vista
  // paidols, and the EM searches simply return empty otherwise.
  const showTypeSelector = useAgave && isEquipmentCodingEnabled;

  // Columns for the picked type (its group + vendor), intersected with the
  // admin-configured visibility. undefined => full flat list (flag off /
  // non-Vista); empty => selector shown but no type chosen yet (only Notes).
  const typeColumns = useMemo<CodingColumnId[] | undefined>(() => {
    if (!showTypeSelector) return undefined;
    if (!selectedCodingType) return [];
    return [...CODING_COLUMNS_BY_TYPE[selectedCodingType], 'vendor' as CodingColumnId].filter((id) =>
      isVisible(id)
    );
  }, [showTypeSelector, selectedCodingType, isVisible]);

  const handleTypeChange = useCallback(
    (next: CodingType | '') => {
      // Empty = deselect → revert to untyped (hides the coding fields). Matches
      // the web type selector's clear option. Clear BOTH type groups too, so a
      // row can't persist as untyped (codingType=null) while still carrying stale
      // equipment/project ids from a type the user just deselected.
      if (!next) {
        setSelectedCodingType(null);
        setValue('codingType', null);
        setValue('projectCode', null);
        setValue('subJob', null);
        setValue('costCode', null);
        setValue('costType', null);
        setValue('equipment', null);
        setValue('emCostCode', null);
        setValue('emCostType', null);
        return;
      }
      setSelectedCodingType(next);
      setValue('codingType', next);
      // Clear the other type's group so stale codes don't persist (GL Code +
      // vendor are common to every type and never cleared).
      if (next !== 'project') {
        setValue('projectCode', null);
        setValue('subJob', null);
        setValue('costCode', null);
        setValue('costType', null);
      }
      if (next !== 'equipment') {
        setValue('equipment', null);
        setValue('emCostCode', null);
        setValue('emCostType', null);
      }
    },
    [setValue]
  );

  const canEdit = useCanEditTransaction(useFlex && !useAgave ? 'flex' : 'connected');
  const isTransactionLocked = !canEdit(transaction);

  // SEE-835: on Spectrum direct connects the AP invoice always posts against the
  // Connections posting vendor; the per-transaction vendor is dropped. Pre-fill +
  // lock the vendor field to that vendor so the UI matches what posts (display-only
  // — the stored coding is untouched). Vista is excluded (its line Supplier is real).
  const spectrumVendorLock = useSpectrumPostingVendorLock();
  const displayedVendor = spectrumVendorLock.locked ? spectrumVendorLock.vendor : watchedVendor;

  // Fuel capture state — raw API units: gallons ×1000, cost in cents, odometer integer
  const {
    vehicleId,
    setVehicleId,
    odometerReading,
    setOdometerReading,
    gallons,
    setGallons,
    setManualCostPerGallon,
    calculatedCostPerGallon,
    displayCostPerGallon,
    isManualCostPerGallon,
    isFuelCaptureEnabled,
    isFuelTransaction,
    isMissingFuelFields,
    resetFuelState,
  } = useFuelCaptureState({ transaction, open });

  // Common styles for fuel capture input fields
  const fuelInputSx = {
    mt: 1,
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
    '& .MuiOutlinedInput-input': { py: 1.5, fontSize: '1.5rem' },
  };

  // Review page styles
  const reviewContainerStyle = {
    px: 2,
    py: 3,
    gap: 2,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 4,
  };

  const reviewFieldStyle = {
    borderBottom: '1px solid',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    pb: 2,
  };

  const reviewFieldStyleWithHeight = {
    ...reviewFieldStyle,
    minHeight: '64px',
  };

  const reviewSectionTitleStyle = {
    color: 'common.white',
    fontWeight: 'bold',
    mb: -2,
  };

  // Reusable step header with progress bar
  const StepHeader = ({
    title,
    currentStepNum,
    totalSteps,
    filledSegments,
  }: {
    title: string;
    currentStepNum: number;
    totalSteps: number;
    filledSegments: number;
  }) => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('mobileTransactionEdit.step', 'Step')} {currentStepNum}/{totalSteps}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: filledSegments > 0 ? 0.5 : 0, mt: 1 }}>
        {filledSegments > 0 && (
          <Box sx={{ flex: filledSegments, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
        )}
        <Box
          sx={{
            flex: totalSteps - filledSegments,
            height: 4,
            borderRadius: 2,
            bgcolor: 'primary.lighter',
          }}
        />
      </Box>
    </Box>
  );

  // Get ERP-specific coding values (only show values from the active ERP)
  const getCodingValue = useMemo(() => {
    if (!transaction) return null;
    return {
      glCode: useAgave ? transaction.agaveGlCode : useFlex ? transaction.flexGlCode : null,
      projectCode: useAgave ? transaction.agaveProject : useFlex ? transaction.flexCode : null,
      costCode: useAgave ? transaction.agaveCostCode : useFlex ? transaction.flexCostCode : null,
      costType: useAgave ? transaction.agaveCostType : useFlex ? transaction.flexCostType : null,
      subJob: useAgave ? transaction.agaveSubJob : null,
      vendor: useAgave ? transaction.agaveVendor : useFlex ? transaction.flexVendor : null,
      location: useAgave ? transaction.agaveLocation : null,
      department: useAgave ? transaction.agaveDepartment : null,
      subaccount: useAgave ? transaction.agaveSubaccount : null,
      employee: useAgave ? transaction.agaveEmployee : null,
      // SEE-1135 — reconstruct the ErpPostingAccount from the flat row fields so
      // getDisplayName can render "Name - (ITEMID)".
      billableItem:
        useAgave && transaction.erpBillableItemId
          ? {
              accountId: transaction.erpBillableItemId,
              accountName: transaction.erpBillableItemName ?? transaction.erpBillableItemId,
            }
          : null,
      equipment: useAgave ? transaction.agaveEquipment : null,
      emCostCode: useAgave ? transaction.agaveEmCostCode : null,
      emCostType: useAgave ? transaction.agaveEmCostType : null,
    };
  }, [transaction, useAgave, useFlex]);

  const handleFileSelect = useCallback(
    (file: File) => {
      setValue('receiptFile', file);
    },
    [setValue]
  );

  // Track previous project ID to detect project changes
  const previousProjectIdRef = useRef<string | null>(transaction?.agaveProject?.id || null);

  // Watch for project/cost code changes and clear incompatible selections
  useEffect(() => {
    if (!useAgave) return;

    const subscription = form.watch((value, { name, type }) => {
      // Check if this is Sage Intacct (has different dependency rules)
      const isSageIntacct = agaveAccount?.source_system?.slug === AgaveSourceSystem.SAGE_INTACCT;

      // When project changes, validate dependent fields
      if (name === 'projectCode') {
        const newProject = value.projectCode;
        // SEE-1899: re-picking the project already set changes nothing, so it clears nothing.
        if ((newProject?.id ?? null) === previousProjectIdRef.current) return;
        const costCode = value.costCode;
        const subJob = value.subJob;
        const location = value.location;
        const department = value.department;
        const previousProjectId = previousProjectIdRef.current;

        let needsToClearCostCode = false;

        // Clear subjob if it doesn't belong to the new project
        if (subJob && 'projectId' in subJob) {
          if (subJob.projectId !== newProject?.id) {
            setValue('subJob', null);
          }
        }

        // Check if cost code has a specific projectId that doesn't match
        if (costCode && 'projectId' in costCode) {
          if (costCode.projectId && costCode.projectId !== newProject?.id) {
            needsToClearCostCode = true;
          }
        }

        // General fallback: Clear cost code/type when project changes (even if projectId not set)
        // This handles the case where cost codes don't have projectId but are still project-specific
        // Only do this if we're actually changing between two projects (not clearing project)
        if (!needsToClearCostCode && costCode && newProject?.id && previousProjectId) {
          // Check if this is a change from one project to another
          if (previousProjectId !== newProject.id) {
            needsToClearCostCode = true;
          }
        }

        // Update the ref for next change
        previousProjectIdRef.current = newProject?.id || null;

        // Apply the clearing
        if (needsToClearCostCode) {
          setValue('costCode', null);
          setValue('costType', null);
        }

        // For Sage Intacct: Department, Location are dependent on Project
        // When project changes, clear these dependent fields
        if (isSageIntacct && previousProjectId && newProject?.id && previousProjectId !== newProject.id) {
          if (location) {
            setValue('location', null);
          }
          if (department) {
            setValue('department', null);
          }
        }
      }

      // When cost code changes, validate cost type
      // Sage 300 CRE and Sage Intacct both have cost type dependent on cost code
      if (name === 'costCode') {
        const costCode = value.costCode as AgaveCostCode | null;
        const costType = value.costType;
        const isSage300CRE = agaveAccount?.source_system?.slug === AgaveSourceSystem.SAGE_300_CRE;

        if (!costCode && costType) {
          // Cost code was cleared, clear cost type too
          setValue('costType', null);
        } else if (isSageIntacct && costCode && costType) {
          // For Sage Intacct: Cost Type is dependent on Cost Code
          // When cost code changes, always clear cost type
          setValue('costType', null);
        } else if (isSage300CRE && costCode && costType && costType.id) {
          // For Sage 300 CRE: validate relationship using cost_type_ids
          // Use runtime check for cost_type_ids (exists in backend but not in TS types yet)
          const costCodeAny = costCode as any;
          if (costCodeAny.cost_type_ids && Array.isArray(costCodeAny.cost_type_ids)) {
            const costCodeCostTypeIds = costCodeAny.cost_type_ids as string[];
            const shouldKeepCostType =
              costCodeCostTypeIds.length > 0 && costCodeCostTypeIds.includes(costType.id);

            if (!shouldKeepCostType) {
              setValue('costType', null);
            }
          }
        }
      }

      // SEE-944 — when employee changes and the paidol toggle is on, auto-fill
      // the location field if the employee maps to exactly one location.
      // Multiple → leave it for the user to pick from the (narrowed) location
      // dropdown. Zero → no auto-fill, full list shows.
      if (name === 'employee') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const erpSetting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
        const shouldAutoFill = Boolean(
          erpSetting?.isEnabled &&
            erpSetting?.locationEnabled &&
            erpSetting?.filterProjectsByEmployeeLocationEnabled
        );
        const employee = value.employee;
        const currentLocationId = value.location?.id;
        if (shouldAutoFill && employee?.id && selectedPaidolId) {
          const employeeId = employee.id;
          const paidolId = selectedPaidolId;
          (async () => {
            try {
              const result = await agaveService.v2SearchAgaveLocations({
                input: {
                  filter: { paidolId, employeeId },
                  limit: 2,
                },
              });
              const items = (result?.items ?? []) as AgaveLocation[];
              // Skip setValue when the resolved location matches what's already
              // on the form — the location watch would otherwise fire and clear
              // a still-valid project.
              if (items.length === 1 && items[0].id !== currentLocationId) {
                setValue('location', items[0]);
              }
            } catch (e) {
              captureException(e);
            }
          })();
        }
      }

      // SEE-944 — when location changes and the toggle is on, clear the
      // project so a stale project from a different location doesn't survive
      // the swap.
      if (name === 'location') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const erpSetting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
        const shouldCascadeClear = Boolean(
          erpSetting?.isEnabled &&
            erpSetting?.locationEnabled &&
            erpSetting?.filterProjectsByEmployeeLocationEnabled
        );
        if (shouldCascadeClear && value.projectCode) {
          setValue('projectCode', null);
        }
      }

      // When subjob changes, validate cost code
      if (name === 'subJob') {
        const subJob = value.subJob as AgaveSubJob | null;
        const costCode = value.costCode;

        // Check if we need to clear the cost code due to incompatibility
        if (subJob && costCode && costCode.id) {
          const subjobCostCodeIds = subJob.cost_code_ids || [];
          const currentCostCodeId = costCode.id;

          // If the subjob has cost code restrictions and the current cost code is not in the list
          if (subjobCostCodeIds.length > 0 && !subjobCostCodeIds.includes(currentCostCodeId)) {
            // Cost code doesn't belong to this subjob, clear it
            setValue('costCode', null);
            setValue('costType', null);
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    form,
    setValue,
    useAgave,
    agaveAccount?.source_system?.slug,
    selectedPaidol,
    selectedPaidolId,
    dispatch,
  ]);

  const handleOpenOptions = useCallback((field: CodingOptionKey) => {
    setCurrentField(field);
    setOptionMenuOpen(true);
  }, []);

  const handleClearOption = useCallback(
    (field: CodingOptionKey) => {
      // ERP-specific clearing logic - matches the filtering dependencies from ERP SyncConfig
      // See: amplify/backend/function/v2Agave/src/agave_api/erp/*.py for ERP configs
      const isSageIntacct = agaveAccount?.source_system?.slug === AgaveSourceSystem.SAGE_INTACCT;
      const isSage300CRE = agaveAccount?.source_system?.slug === AgaveSourceSystem.SAGE_300_CRE;
      const isQuickbooksDesktop = agaveAccount?.source_system?.slug === AgaveSourceSystem.QUICKBOOKS_DESKTOP;
      const isAcumatica = agaveAccount?.source_system?.slug === AgaveSourceSystem.ACUMATICA;

      // Cost codes are fetched per_project for Sage 300 CRE and Sage Intacct only
      const costCodesFilteredByProject = isSage300CRE || isSageIntacct;
      // SubJobs: Sage 300 CRE, QuickBooks Desktop, Acumatica (project tasks)
      const hasSubJobs = isSage300CRE || isQuickbooksDesktop || isAcumatica;
      // Cost types are filtered by project/cost code for Sage 300 CRE and Sage Intacct
      const costTypesFilteredByProjectOrCostCode = isSage300CRE || isSageIntacct;
      // Locations and departments are filtered by project for Sage Intacct only
      const locationsDepartmentsFilteredByProject = isSageIntacct;

      const useFlexStructured = isStructuredMode && useFlex;

      // Flex structured-mode dependent clearing — MUST stay in sync with
      // GL_CODE_DEPENDENTS / PROJECT_DEPENDENTS in app/hooks/useFlexStructuredMode.ts.
      // If the desktop list changes there, update the two blocks below too.
      switch (field) {
        case 'glCode':
          setValue('glCode', null);
          if (useFlexStructured) {
            setValue('projectCode', null);
            setValue('costCode', null);
            setValue('costType', null);
            setValue('vendor', null);
          }
          break;
        case 'project':
          setValue('projectCode', null);
          if (useFlexStructured) {
            setValue('costCode', null);
            setValue('costType', null);
            setValue('vendor', null);
            break;
          }
          // SubJobs: Only Sage 300 CRE and QuickBooks Desktop have subJobs
          if (hasSubJobs) {
            setValue('subJob', null);
          }
          // CostCodes: Only Sage 300 CRE and Sage Intacct filter by project
          if (costCodesFilteredByProject) {
            setValue('costCode', null);
          }
          // CostTypes: Only Sage 300 CRE and Sage Intacct filter by project
          if (costTypesFilteredByProjectOrCostCode) {
            setValue('costType', null);
          }
          // Locations/Departments: Only Sage Intacct filters by project
          if (locationsDepartmentsFilteredByProject) {
            setValue('location', null);
            setValue('department', null);
          }
          break;
        case 'subJob':
          setValue('subJob', null);
          break;
        case 'costCode':
          setValue('costCode', null);
          // CostTypes: Only Sage 300 CRE and Sage Intacct filter by cost code
          if (costTypesFilteredByProjectOrCostCode) {
            setValue('costType', null);
          }
          break;
        case 'costType':
          setValue('costType', null);
          break;
        case 'vendor':
          setValue('vendor', null);
          break;
        case 'location':
          setValue('location', null);
          break;
        case 'department':
          setValue('department', null);
          break;
        case 'subaccount':
          setValue('subaccount', null);
          break;
        case 'employee':
          setValue('employee', null);
          break;
        case 'billableItem':
          setValue('billableItem', null);
          break;
        case 'equipment':
          // Clearing equipment clears its EM-group-scoped children too.
          setValue('equipment', null);
          setValue('emCostCode', null);
          setValue('emCostType', null);
          break;
        case 'emCostCode':
          setValue('emCostCode', null);
          break;
        case 'emCostType':
          setValue('emCostType', null);
          break;
      }
    },
    [setValue, agaveAccount?.source_system?.slug, isStructuredMode, useFlex]
  );

  const handleSelectOption = useCallback(
    (option: CodingOptionType) => {
      if (!currentField) return;

      switch (currentField) {
        case 'glCode': {
          const newGlCode = option as FlexGlCode | AgaveGlCode;
          const currentGlCode = form.getValues().glCode;
          setValue('glCode', newGlCode);
          // Flex structured-mode: mirror GL_CODE_DEPENDENTS in app/hooks/useFlexStructuredMode.ts.
          if (isStructuredMode && useFlex && newGlCode?.id !== currentGlCode?.id) {
            setValue('projectCode', null);
            setValue('costCode', null);
            setValue('costType', null);
            setValue('vendor', null);
          }
          break;
        }
        case 'project': {
          const newProject = option as FlexCode | AgaveProject;
          const currentProject = form.getValues().projectCode;
          const currentCostCode = form.getValues().costCode;
          const currentCostType = form.getValues().costType;
          const currentSubJob = form.getValues().subJob;
          const currentLocation = form.getValues().location;
          const currentDepartment = form.getValues().department;
          const isSageIntacct = agaveAccount?.source_system?.slug === AgaveSourceSystem.SAGE_INTACCT;

          setValue('projectCode', newProject);

          // SEE-1899: re-picking the project already set changes nothing, so it clears nothing.
          if (newProject?.id && newProject.id === currentProject?.id) break;

          // Flex structured-mode: mirror PROJECT_DEPENDENTS in app/hooks/useFlexStructuredMode.ts.
          if (isStructuredMode && useFlex && newProject?.id !== currentProject?.id) {
            setValue('costCode', null);
            setValue('costType', null);
            setValue('vendor', null);
            break;
          }

          // Clear subjob if it doesn't belong to the new project
          if (currentSubJob && 'projectId' in currentSubJob) {
            if (currentSubJob.projectId !== newProject?.id) {
              setValue('subJob', null);
            }
          }

          // Clear cost code if it doesn't belong to the new project
          if (currentCostCode && 'projectId' in currentCostCode) {
            if (currentCostCode.projectId && currentCostCode.projectId !== newProject?.id) {
              setValue('costCode', null);
              setValue('costType', null); // Also clear cost type if clearing cost code
            }
          }

          // For Sage Intacct: Department, Location, CostCode, and CostType are all dependent on Project
          // When project changes, clear all these dependent fields
          if (isSageIntacct) {
            // Always clear cost code and cost type when project changes
            if (currentCostCode) {
              setValue('costCode', null);
            }
            if (currentCostType) {
              setValue('costType', null);
            }
            // Clear location and department
            if (currentLocation) {
              setValue('location', null);
            }
            if (currentDepartment) {
              setValue('department', null);
            }
          }

          // If cost code wasn't cleared but cost type exists, validate cost type
          if (currentCostType && form.getValues().costCode) {
            // Cost type validation will be handled when filters change
          }
          break;
        }
        case 'costCode': {
          const newCostCode = option as FlexCostCode | AgaveCostCode;
          const currentCostType = form.getValues().costType;
          const isSageIntacct = agaveAccount?.source_system?.slug === AgaveSourceSystem.SAGE_INTACCT;
          const isSage300CRE = agaveAccount?.source_system?.slug === AgaveSourceSystem.SAGE_300_CRE;

          setValue('costCode', newCostCode);

          // For Sage Intacct: Cost Type is dependent on Cost Code
          // When cost code changes, always clear cost type
          if (isSageIntacct && currentCostType) {
            setValue('costType', null);
          } else if (isSage300CRE && currentCostType && currentCostType.id) {
            // For Sage 300 CRE: validate relationship using cost_type_ids
            // Use runtime check for cost_type_ids (exists in backend but not in TS types yet)
            const costCodeAny = newCostCode as any;
            if (costCodeAny.cost_type_ids && Array.isArray(costCodeAny.cost_type_ids)) {
              const costCodeCostTypeIds = costCodeAny.cost_type_ids as string[];
              const currentCostTypeId = currentCostType.id;

              // Clear cost type if it's not valid for the new cost code
              const shouldKeepCostType =
                costCodeCostTypeIds.length > 0 && costCodeCostTypeIds.includes(currentCostTypeId);

              if (!shouldKeepCostType) {
                setValue('costType', null);
              }
            }
          }
          break;
        }
        case 'costType':
          setValue('costType', option as FlexCostType | AgaveCostType);
          break;
        case 'subJob': {
          const newSubJob = option as AgaveSubJob;
          const currentCostCode = form.getValues().costCode;

          setValue('subJob', newSubJob);

          // Check if we need to clear the cost code due to incompatibility
          if (newSubJob && currentCostCode && currentCostCode.id) {
            const subjobCostCodeIds = newSubJob.cost_code_ids || [];
            const currentCostCodeId = currentCostCode.id;

            // If the subjob has cost code restrictions and the current cost code is not in the list
            if (subjobCostCodeIds.length > 0 && !subjobCostCodeIds.includes(currentCostCodeId)) {
              setValue('costCode', null);
              setValue('costType', null);
            }
          }
          break;
        }
        case 'vendor':
          setValue('vendor', option as FlexVendor | AgaveVendor);
          break;
        case 'location':
          setValue('location', option as AgaveLocation);
          break;
        case 'department':
          setValue('department', option as AgaveDepartment);
          break;
        case 'subaccount':
          setValue('subaccount', option as AgaveSubaccount);
          break;
        case 'employee':
          setValue('employee', option as AgaveEmployeeEntity);
          break;
        case 'billableItem':
          setValue('billableItem', option as ErpPostingAccount);
          break;
        case 'equipment':
          // EM cost code/type are scoped by the equipment's emGroup, so a new
          // equipment invalidates them.
          setValue('equipment', option as AgaveEquipment);
          setValue('emCostCode', null);
          setValue('emCostType', null);
          break;
        case 'emCostCode':
          setValue('emCostCode', option as AgaveEmCostCode);
          break;
        case 'emCostType':
          setValue('emCostType', option as AgaveEmCostType);
          break;
      }
      setOptionMenuOpen(false);
      setCurrentField(null);
    },
    [currentField, setValue, form, agaveAccount?.source_system?.slug, isStructuredMode, useFlex]
  );

  const handleNext = useCallback(() => {
    if (currentStep === 'details_receipt') {
      setCurrentStep('coding');
    } else if (currentStep === 'coding') {
      if (isFuelCaptureEnabled && isFuelTransaction) {
        setCurrentStep('fuel_capture');
      } else {
        setCurrentStep('review');
      }
    } else if (currentStep === 'fuel_capture') {
      // Values are already in API units — NumericInput emits raw ×1000/cents/integer
      setValue('fuelVehicleId', vehicleId || null);
      setValue('fuelGallons', gallons);
      setValue('fuelOdometerReading', odometerReading);
      setValue('fuelCostPerGallon', displayCostPerGallon);
      setCurrentStep('review');
    }
  }, [
    currentStep,
    isFuelCaptureEnabled,
    isFuelTransaction,
    vehicleId,
    gallons,
    odometerReading,
    displayCostPerGallon,
    setValue,
  ]);

  // A finalized (posted/bypassed/approved) transaction never resubmits, so it should
  // not read as "returned" in the CTA/message even if returnedStatus lingers.
  const isReturnedActionable =
    isReturned(transaction?.returnedStatus) && !isTransactionFinalized(transaction);

  // SEE-1842: what a Submit would still leave missing, from the form as it stands. Only a Save that
  // submits (a New row) or resubmits (a returned one) waits for it.
  const missingBeforeSubmit = useMemo(() => {
    const savingSubmits = isReturnedActionable || transaction?.workflowStatus === WorkflowStatus.NEW;
    if (!transaction || !savingSubmits) return '';
    const blockers = getSubmitBlockers(
      { ...transaction, note, codingType: selectedCodingType },
      selectedPaidol,
      {
        hasAttachedReceipt: receipts.length > 0 || !!stagedReceiptFile,
        coding: watchedByColumn,
      }
    );
    return isSubmitBlocked(blockers) ? describeSubmitBlockers(blockers, t) : '';
  }, [
    isReturnedActionable,
    transaction,
    note,
    selectedCodingType,
    selectedPaidol,
    receipts.length,
    stagedReceiptFile,
    watchedByColumn,
    t,
  ]);

  const handleSave = useCallback(async () => {
    // Saving a returned transaction resubmits it for review — capture before
    // onSubmit, which clears the returned status.
    const wasReturned = isReturnedActionable;
    // If fuel capture step was skipped, set values before saving
    if (!(isFuelCaptureEnabled && isFuelTransaction)) {
      setValue('fuelVehicleId', vehicleId || null);
      setValue('fuelGallons', gallons);
      setValue('fuelOdometerReading', odometerReading);
      setValue('fuelCostPerGallon', displayCostPerGallon);
    }
    setIsSaving(true);
    let heldBackBy: SubmitBlockers | null;
    try {
      heldBackBy = await onSubmit();
    } catch {
      // onSubmit already surfaced the error; re-enable the page so the user can
      // retry, and skip the success toast and refetch.
      setIsSaving(false);
      return;
    }
    // Success: onSubmit has closed the sheet; leave isSaving true so the overlay
    // covers the teardown frame (the component unmounts, so no stuck state).
    // Reconcile the Actions queue with the server (mirrors the manual refresh that
    // already makes the saved transaction drop off). Runs after onSubmit resolves,
    // so the receipt upload is already committed.
    onSaved?.();
    // SEE-1842: a Save the submit rule held back kept the edits but submitted nothing.
    const message = heldBackBy
      ? t('submitGate.savedMissing', { items: submitBlockerItems(heldBackBy, t) })
      : wasReturned
      ? t('mobileTransactionEdit.resubmittedSuccessfully', 'Transaction resubmitted successfully')
      : t('mobileTransactionEdit.changesSaved', 'All changes saved');
    enqueueSnackbar(message, {
      variant: 'default',
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      autoHideDuration: 3000,
      style: { marginBottom: '80px' },
      action: (snackbarId) => (
        <IconButton size="small" onClick={() => closeSnackbar(snackbarId)} sx={{ color: 'white' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
    });
  }, [
    onSubmit,
    onSaved,
    isReturnedActionable,
    isFuelCaptureEnabled,
    isFuelTransaction,
    vehicleId,
    gallons,
    odometerReading,
    displayCostPerGallon,
    setValue,
    enqueueSnackbar,
    closeSnackbar,
    t,
  ]);

  const handleBack = useCallback(() => {
    if (currentStep === 'review') {
      if (isFuelCaptureEnabled && isFuelTransaction) {
        setCurrentStep('fuel_capture');
      } else {
        setCurrentStep('coding');
      }
    } else if (currentStep === 'fuel_capture') {
      setCurrentStep('coding');
    } else if (currentStep === 'coding') {
      setCurrentStep('details_receipt');
    } else {
      onClose();
    }
  }, [currentStep, onClose, isFuelCaptureEnabled, isFuelTransaction]);

  useEffect(() => {
    if (!open) {
      setCurrentStep('details_receipt');
      setOptionMenuOpen(false);
      setCurrentField(null);
      setNote('');
      resetFuelState();
      form.reset();
      dispatch(clearAllFlexCodingSearches());
      setIsSaving(false);
    }
  }, [open, form, resetFuelState, dispatch]);

  useEffect(() => {
    if (open && transaction && getCodingValue) {
      form.reset({
        receiptFile: undefined,
        note: transaction.note ?? '',
        glCode: getCodingValue.glCode,
        projectCode: getCodingValue.projectCode,
        costCode: getCodingValue.costCode,
        costType: getCodingValue.costType,
        subJob: getCodingValue.subJob,
        vendor: getCodingValue.vendor,
        location: getCodingValue.location,
        department: getCodingValue.department,
        subaccount: getCodingValue.subaccount,
        employee: getCodingValue.employee,
        // SEE-1899: the save diffs against these values, so a billable item left out here would
        // read as never set, and clearing it would not save.
        billableItem: getCodingValue.billableItem,
        equipment: getCodingValue.equipment,
        emCostCode: getCodingValue.emCostCode,
        emCostType: getCodingValue.emCostType,
        codingType: transaction.codingType ?? null,
        fuelVehicleId: transaction.fuelVehicleId ?? null,
        fuelGallons: transaction.fuelGallons ?? null,
        fuelOdometerReading: transaction.fuelOdometerReading ?? null,
        fuelCostPerGallon: transaction.fuelCostPerGallon ?? null,
      });
      // SEE-1899: the sheet stays mounted across rows, so the watcher compares a pick with this
      // row's project, not the last row's.
      previousProjectIdRef.current = getCodingValue.projectCode?.id ?? null;
      setNote(transaction.note ?? '');
      // SEE-1091 — seed the type selector from the persisted/ derived type.
      setSelectedCodingType(resolveDisplayCodingType(transaction));
    }
  }, [open, transaction, form, getCodingValue]);

  // Receipts from the ReceiptUpload array (new system only) that have a stored file
  const receiptsToLoad = useMemo(
    () => receipts.filter((receipt) => receipt?.document && receipt.document.trim().length > 0),
    [receipts]
  );

  // Load all receipt URLs in parallel
  useEffect(() => {
    const loadReceiptUrls = async () => {
      if (!open || receiptsToLoad.length === 0) {
        setReceiptImages([]);
        return;
      }

      try {
        // Load all receipt URLs in parallel
        const urls = await Promise.all(
          receiptsToLoad.map(async (receipt) => {
            try {
              return await fileAccessService.getDownloadUrl(receipt.document);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(`[MobileEditTransactionSheet] Error loading receipt path:`, err);
              return null as string | null;
            }
          })
        );

        // Drop receipts whose URL failed or came back empty, keeping each URL with its receipt
        const images = receiptsToLoad.flatMap((receipt, i) => {
          const url = urls[i];
          return typeof url === 'string' && url.trim().length > 0 ? [{ receipt, url }] : [];
        });

        setReceiptImages(images);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[MobileEditTransactionSheet] Error loading receipts:', error);
        enqueueSnackbar('Error loading receipt images', { variant: 'error' });
        setReceiptImages([]);
      }
    };

    loadReceiptUrls();
  }, [open, receiptsToLoad, enqueueSnackbar]);

  const handleReceiptDelete = useCallback(
    async (index: number) => {
      const receipt = receiptImages[index]?.receipt;
      if (!receipt?.id || !receipt?.document) return;

      setIsDeletingReceipt(true);
      try {
        await deleteReceipt({
          document: receipt.document,
          receiptId: receipt.id,
          paidolId: selectedPaidolId,
          transactionId: transaction!.id,
          isLastReceipt: receipts.length <= 1,
        });
        await refetchReceipts();
        enqueueSnackbar(t('mobileTransactionDetails.receiptDeletedSuccessfully'), { variant: 'success' });
      } catch (err) {
        captureException(err, {
          tags: { component: 'MobileEditTransactionSheet', action: 'handleReceiptDelete' },
          extra: { receiptId: receipt.id },
        });
        enqueueSnackbar(t('mobileTransactionDetails.errorDeletingReceipt'), { variant: 'error' });
      } finally {
        setIsDeletingReceipt(false);
      }
    },
    [
      receiptImages,
      receipts.length,
      deleteReceipt,
      selectedPaidolId,
      transaction?.id,
      refetchReceipts,
      enqueueSnackbar,
      t,
    ]
  );

  if (!open || !transaction || isTransactionLocked) return null;

  const costCodeAndTypeRequireProject =
    useAgave && Boolean(selectedPaidol?.requireProjectBeforeCostCode?.isEnabled) && !watchedProjectCode;

  return (
    <>
      <MobileLoadingOverlay isLoading={isSubmitting || isSaving} invert={currentStep === 'review'} />
      <MobilePageContent
        transparentHeight={currentStep === 'review' ? BACKGROUND_SIZES.FULL : BACKGROUND_SIZES_v2.SHOW_HEADER}
        title={currentStep === 'review' ? '' : t('mobileTransactionEdit.transactionDetails')}
        onBack={currentStep === 'review' ? undefined : handleBack}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flex: 1,
            height: '100%',
          }}
        >
          {currentStep === 'details_receipt' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <StepHeader
                title={t('mobileTransactionEdit.transaction', 'Transaction')}
                currentStepNum={1}
                totalSteps={isFuelCaptureEnabled && isFuelTransaction ? 3 : 2}
                filledSegments={0}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {t('mobileTransactionEdit.card')}
                </Typography>
                <Typography variant="body1">
                  {transaction.card ? (
                    <>
                      <span {...sentryMask}>{transaction.card.name || ''}</span>
                      {` - ${transaction.card.last4}`}
                    </>
                  ) : (
                    ''
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {t('mobileTransactionEdit.amount')}
                </Typography>
                <Typography variant="body1">
                  {formatCurrency({ value: transaction.approvedAmount?.value || 0 })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {t('mobileTransactionEdit.merchant')}
                </Typography>
                <Typography variant="body1">{transaction.merchantName || ''}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {t('mobileTransactionEdit.transactionDate')}
                </Typography>
                <Typography variant="body1">
                  {authorizationDate
                    ? formatDateInEasternWithDateFns(authorizationDate, 'MM/dd/yy')
                    : t('mobileTransactionEdit.noDate')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {t('mobileTransactionEdit.postedDate')}
                </Typography>
                <Typography variant="body1">
                  {transaction.status === HighnoteTransactionStatus.APPROVED
                    ? t('N/A')
                    : transaction.eventDate
                    ? formatDateInEasternWithDateFns(transaction.eventDate, 'MM/dd/yy')
                    : t('mobileTransactionEdit.noDate')}
                </Typography>
              </Box>
              <Box>
                {receiptUrls.length > 0 && (
                  <TransactionField label={t('mobileTransactionEdit.receipt')} value="" />
                )}
                <MobileReceiptPreview
                  viewOnly={false}
                  scopeId={scopeId}
                  receiptUrls={receiptUrls || []}
                  onFileSelect={handleFileSelect}
                  onReceiptDelete={handleReceiptDelete}
                  isDeletingReceipt={isDeletingReceipt}
                />
              </Box>
            </Box>
          )}
          {currentStep === 'coding' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <StepHeader
                title={t('mobileTransactionDetails.projectCodes', 'Project Codes')}
                currentStepNum={2}
                totalSteps={isFuelCaptureEnabled && isFuelTransaction ? 3 : 2}
                filledSegments={1}
              />
              {showTypeSelector && (
                <FormControl fullWidth>
                  <Select
                    value={selectedCodingType ?? ''}
                    displayEmpty
                    // "Transaction type" is the placeholder shown when nothing is
                    // picked — via renderValue, NOT a list item. The options are
                    // only GL / Project / Equipment.
                    renderValue={(value) => {
                      const labels: Record<string, string> = {
                        gl: t('transactionSidesheet.gl', 'GL'),
                        project: t('transactionSidesheet.project', 'Project'),
                        equipment: t('transactionSidesheet.equipment', 'Equipment'),
                      };
                      if (value && labels[value as string]) return labels[value as string];
                      return (
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                          {t('transactionSidesheet.transactionType', 'Transaction type')}
                        </Typography>
                      );
                    }}
                    onChange={(e) => handleTypeChange(e.target.value as CodingType | '')}
                  >
                    {/* Options are only the three types — no "Transaction type"
                        row. Click the active option again to toggle it off
                        (deselect); MUI Select doesn't fire onChange on a re-click
                        of the current value, so clear it via onClick. */}
                    {(['gl', 'project', 'equipment'] as CodingType[]).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        onClick={() => {
                          if (type === selectedCodingType) handleTypeChange('');
                        }}
                      >
                        {t(
                          `transactionSidesheet.${type}`,
                          type === 'gl' ? 'GL' : type === 'project' ? 'Project' : 'Equipment'
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <MobileCodingFieldList
                mode="editable"
                isVisible={isVisible}
                getLabel={getLabel}
                orderedColumnIds={typeColumns}
                currentValues={getCodingValue}
                watchedValues={{ ...watchedByColumn, vendor: displayedVendor }}
                onOpenOptions={handleOpenOptions}
                onClearOption={handleClearOption}
                disabledOptionKeys={[
                  ...(costCodeAndTypeRequireProject ? (['costCode', 'costType'] as const) : []),
                  ...(spectrumVendorLock.locked ? (['vendor'] as const) : []),
                ]}
              />
              <TransactionField
                label={t('mobileTransactionEdit.notes')}
                value={t('mobileTransactionEdit.notesPlaceholder')}
              />
              <MobileNoteInput
                maxLength={1000}
                value={note}
                onChange={(e) => {
                  const newNote = e.target.value;
                  setNote(newNote);
                  form.setValue('note', newNote);
                }}
              />
            </Box>
          )}
          {currentStep === 'fuel_capture' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <StepHeader
                title={t('mobileTransactionDetails.fuelCapture', 'Fuel Capture')}
                currentStepNum={3}
                totalSteps={3}
                filledSegments={2}
              />
              {/* Vehicle ID */}
              <Box>
                <Typography variant="body1" color="text.primary">
                  {t('mobileTransactionEdit.vehicleId', 'Vehicle ID')}
                </Typography>
                <TextField
                  fullWidth
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  placeholder={t('mobileTransactionEdit.enterVehicleId', 'Enter vehicle ID')}
                  variant="outlined"
                  size="small"
                  sx={fuelInputSx}
                />
              </Box>

              {/* Odometer Reading */}
              <Box>
                <Typography variant="body1" color="text.primary">
                  {t('mobileTransactionEdit.odometerReading', 'Odometer Reading')}
                </Typography>
                <NumericInput
                  fullWidth
                  value={odometerReading}
                  onChange={setOdometerReading}
                  placeholder="0"
                  size="small"
                  decimalPlaces={0}
                  maxValue={99999999}
                  suffix={t('miles', 'miles')}
                  sx={fuelInputSx}
                />
              </Box>

              {/* Gallons */}
              <Box>
                <Typography variant="body1" color="text.primary">
                  {t('mobileTransactionEdit.gallons', 'Gallons')}
                </Typography>
                <NumericInput
                  fullWidth
                  value={gallons}
                  onChange={setGallons}
                  placeholder="0.000"
                  size="small"
                  decimalPlaces={3}
                  maxValue={999999000}
                  suffix={t('gal', 'gal')}
                  sx={fuelInputSx}
                />
              </Box>

              {/* Cost Per Gallon */}
              <Box>
                <Typography variant="body1" color="text.primary">
                  {t('mobileTransactionEdit.costPerGallon', 'Cost Per Gallon')}
                </Typography>
                <NumericInput
                  fullWidth
                  value={displayCostPerGallon}
                  onChange={setManualCostPerGallon}
                  placeholder="0.00"
                  size="small"
                  decimalPlaces={2}
                  maxValue={99999900}
                  showCurrency
                  suffix={t('perGal', '/gal')}
                  sx={fuelInputSx}
                />
                {isManualCostPerGallon && !!calculatedCostPerGallon && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, display: 'block', color: 'text.secondary', cursor: 'pointer' }}
                    onClick={() => setManualCostPerGallon(null)}
                  >
                    {t('mobileTransactionEdit.useCalculatedValue', 'Use calculated value')}:{' '}
                    {formatCurrency({ value: calculatedCostPerGallon })}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {/* Review Step */}
          {currentStep === 'review' && transaction && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 5 }}>
              {/* Transaction Details Box */}
              <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
                {t('mobileTransactionDetails.title', 'Transaction Details')}
              </Typography>
              <Box sx={reviewContainerStyle}>
                <TransactionField
                  label={t('mobileTransactionDetails.card')}
                  value={
                    transaction.card ? (
                      <>
                        <span {...sentryMask}>{transaction.card.name || ''}</span>
                        {` - ${transaction.card.last4}`}
                      </>
                    ) : (
                      ''
                    )
                  }
                  sx={reviewFieldStyle}
                  invertColor
                />
                <TransactionField
                  label={t('mobileTransactionDetails.merchant')}
                  value={transaction.merchantName || ''}
                  sx={reviewFieldStyle}
                  invertColor
                />
                <TransactionField
                  label={t('mobileTransactionDetails.amount')}
                  value={formatCurrency({ value: transaction.approvedAmount?.value || 0 })}
                  invertColor
                />
                <TransactionField
                  label={t('mobileTransactionDetails.transactionDate')}
                  value={
                    authorizationDate
                      ? formatDateInEastern(authorizationDate)
                      : t('mobileTransactionDetails.noDate')
                  }
                  sx={reviewFieldStyle}
                  invertColor
                />
                <TransactionField
                  label={t('mobileTransactionDetails.postedDate')}
                  value={
                    transaction.status === HighnoteTransactionStatus.APPROVED
                      ? t('N/A')
                      : transaction.eventDate
                      ? formatDateInEastern(transaction.eventDate)
                      : t('mobileTransactionDetails.noDate')
                  }
                  sx={reviewFieldStyle}
                  invertColor
                />
              </Box>

              {/* Project Codes Box */}
              <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
                {t('mobileTransactionDetails.projectCodes', 'Project Codes')}
              </Typography>
              <Box sx={reviewContainerStyle}>
                <MobileCodingFieldList
                  mode="readonly"
                  isVisible={isVisible}
                  getLabel={getLabel}
                  orderedColumnIds={typeColumns}
                  rawWatchedValues={{ ...watchedByColumn, vendor: displayedVendor }}
                  getDisplayName={(value) => getDisplayName(value as any)}
                  emptyValue="--"
                  fieldSx={reviewFieldStyleWithHeight}
                  invertColor
                />
                <TransactionField
                  label={t('mobileTransactionDetails.notes')}
                  value={<span {...sentryMask}>{note || '--'}</span>}
                  invertColor
                />
              </Box>

              {/* Fuel Capture Box */}
              {isFuelCaptureEnabled && isFuelTransaction && (
                <>
                  <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
                    {t('mobileTransactionDetails.fuelCapture', 'Fuel Capture')}
                  </Typography>
                  <Box sx={reviewContainerStyle}>
                    <TransactionField
                      label={t('mobileTransactionDetails.vehicleId', 'Vehicle ID')}
                      value={vehicleId || '--'}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    <TransactionField
                      label={t('mobileTransactionDetails.odometerReading', 'Odometer')}
                      value={odometerReading ? `${odometerReading} ${t('miles', 'miles')}` : '--'}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    <TransactionField
                      label={t('mobileTransactionDetails.gallons', 'Gallons')}
                      value={gallons != null ? `${(gallons / 1000).toFixed(3)} ${t('gal', 'gal')}` : '--'}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    <TransactionField
                      label={t('mobileTransactionDetails.costPerGallon', 'Cost Per Gallon')}
                      value={
                        displayCostPerGallon != null
                          ? `${formatCurrency({ value: displayCostPerGallon })}${t('perGal', '/gal')}`
                          : '--'
                      }
                      invertColor
                    />
                  </Box>
                </>
              )}

              {/* SEE-1842: what Submit would leave missing, said before it is pressed. */}
              {missingBeforeSubmit && (
                <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 'bold' }}>
                  {missingBeforeSubmit}
                </Typography>
              )}
            </Box>
          )}
          {/* Navigation Footer */}
          <Box
            sx={{
              backgroundColor: 'primary.lighter',
              borderRadius: 10,
              py: 1,
              px: 1.5,
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: 400,
              alignSelf: 'center',
              width: '100%',
            }}
          >
            <Typography
              onClick={onClose}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                cursor: 'pointer',
                px: 2,
                py: 1,
              }}
            >
              {t('mobileTransactionEdit.cancel')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {currentStep !== 'details_receipt' && (
                <Button
                  onClick={handleBack}
                  sx={{
                    borderRadius: 10,
                    px: 3,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  }}
                  variant="outlined"
                >
                  {t('mobileTransactionEdit.back')}
                </Button>
              )}
              <Button
                onClick={currentStep === 'review' ? handleSave : handleNext}
                sx={{
                  borderRadius: 10,
                  px: 3,
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
                variant="contained"
                disabled={
                  isSubmitting ||
                  isSaving ||
                  ((currentStep === 'fuel_capture' || currentStep === 'review') && isMissingFuelFields)
                }
              >
                {currentStep === 'details_receipt'
                  ? t('mobileTransactionEdit.next')
                  : currentStep === 'coding' && isFuelCaptureEnabled && isFuelTransaction
                  ? t('mobileTransactionEdit.next')
                  : currentStep === 'review'
                  ? t('mobileTransactionEdit.submit', 'Submit')
                  : t('mobileTransactionEdit.review', 'Review')}
              </Button>
            </Box>
          </Box>
          {/* Spacer for scrolling */}
          <Box sx={{ minHeight: 20 }} />
        </Box>
      </MobilePageContent>
      <MobileCodingMenu
        open={optionMenuOpen}
        onClose={() => {
          setOptionMenuOpen(false);
          setCurrentField(null);
        }}
        codingType={currentField}
        onSelect={handleSelectOption}
        getLabel={getLabel}
        transaction={transaction}
        selectedGlCode={watchedGlCode}
        selectedProject={watchedProjectCode}
        selectedCostCode={watchedCostCode}
        selectedCostType={watchedCostType}
        selectedSubJob={watchedSubJob}
        selectedVendor={watchedVendor}
        selectedLocation={watchedLocation}
        selectedDepartment={watchedDepartment}
        selectedSubaccount={watchedSubaccount}
        selectedEmployee={watchedEmployee}
        selectedEquipment={watchedEquipment}
        selectedEmCostCode={watchedEmCostCode}
      />
    </>
  );
};

export default MobileEditTransactionSheet;
